import React, { useState } from 'react';
import { Copy, Check } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import PersonalQuotaFields from './PersonalQuotaFields';
import {
  QUOTA_DEFAULTS, QUEUE_CLASSES, QUEUE_CLASS_LABELS, QUEUE_CLASS_ICONS, defaultQueueClass,
} from '../../lib/roles';

function defaultExpiration() {
  const d = new Date();
  d.setDate(d.getDate() + 30);
  return d.toISOString().slice(0, 10);
}

// Плитки типа токена. hint — вторая строка плитки, email/name — плейсхолдеры полей ниже.
const ROLE_TILES = [
  { key: 'service', label: 'Сервисный', hint: 'бессрочный, для интеграций', email: 'bot@service.local', name: 'ASR Bot' },
  { key: 'client', label: 'Клиентский', hint: 'с истечением, платный доступ', email: 'client@example.com', name: 'ООО Клиент' },
  { key: 'partner', label: 'Партнёр', hint: 'офис группы, взаимозачёт', email: 'partner@corp.ru', name: 'ООО Партнёр' },
];

export default function CreateServiceTokenDialog({ open, onClose, onCreate, offices = [] }) {
  const [role, setRole] = useState('service');
  const [email, setEmail] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [office, setOffice] = useState('');
  const [ownerEmail, setOwnerEmail] = useState('');
  const [quotaAudio, setQuotaAudio] = useState('');
  const [quotaSummary, setQuotaSummary] = useState('');
  const [quotaHourly, setQuotaHourly] = useState('');
  const [expiration, setExpiration] = useState(defaultExpiration());
  // Партнёр — тоже контрагент без срока (см. bit-auth: role in (service, partner) → expiration
  // 2099 год), но со своим классом очереди — по умолчанию «Обычный», не «Пакетный» как у service.
  const [queueClass, setQueueClass] = useState(defaultQueueClass('service'));
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState(null);
  const [createdToken, setCreatedToken] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleSelectRole = (key) => {
    setRole(key);
    setQueueClass(defaultQueueClass(key));
  };

  const reset = () => {
    setRole('service');
    setEmail('');
    setDisplayName('');
    setOffice('');
    setOwnerEmail('');
    setQuotaAudio('');
    setQuotaSummary('');
    setQuotaHourly('');
    setExpiration(defaultExpiration());
    setQueueClass(defaultQueueClass('service'));
    setCreating(false);
    setError(null);
    setCreatedToken(null);
    setCopied(false);
  };

  const handleClose = () => {
    const hadToken = !!createdToken;
    reset();
    onClose(hadToken);
  };

  const handleCreate = async () => {
    if (!email.trim()) {
      setError('Email обязателен');
      return;
    }
    if (role === 'client' && !expiration) {
      setError('Для клиента укажите срок действия');
      return;
    }
    // Офис — куда относить расход, ответственный — с кого спрашивать за учётку и ключи.
    if (!office) {
      setError('Укажите офис');
      return;
    }
    if (!ownerEmail.trim()) {
      setError('Укажите ответственного за учётку');
      return;
    }
    setCreating(true);
    setError(null);
    try {
      const data = { email: email.trim(), role, office, owner_email: ownerEmail.trim() };
      if (displayName.trim()) data.display_name = displayName.trim();
      if (quotaAudio) data.quota_audio_minutes = parseInt(quotaAudio, 10);
      if (quotaSummary) data.quota_summary_requests = parseInt(quotaSummary, 10);
      if (quotaHourly) data.quota_summary_per_hour = parseInt(quotaHourly, 10);
      if (role === 'client') {
        data.expiration = new Date(expiration + 'T23:59:59Z').toISOString();
      }
      if (role === 'partner') {
        data.queue_class = queueClass;
      }

      const result = await onCreate(data);
      setCreatedToken(result.token);
    } catch (e) {
      const detail = e.response?.data?.detail || 'Ошибка создания';
      if (e.response?.status === 409) {
        setError('Пользователь с таким email уже существует');
      } else {
        setError(detail);
      }
    } finally {
      setCreating(false);
    }
  };

  const handleCopy = async () => {
    if (createdToken) {
      await navigator.clipboard.writeText(createdToken);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const defaults = QUOTA_DEFAULTS[role];

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Создать токен</DialogTitle>
        </DialogHeader>

        {!createdToken ? (
          <>
            <div className="space-y-4 py-2">
              <div>
                <label className="text-xs text-paper-ink-3 block mb-1">Тип</label>
                <div className="flex gap-2">
                  {ROLE_TILES.map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => handleSelectRole(opt.key)}
                      className={`flex-1 px-3 py-2 rounded-chip border text-sm transition-colors ${
                        role === opt.key
                          ? 'bg-paper-ink text-white'
                          : 'bg-paper-bg text-paper-ink-2 hover:bg-paper-panel'
                      }`}
                    >
                      <div>{opt.label}</div>
                      <div className="text-[10px] font-normal text-paper-hint mt-0.5">{opt.hint}</div>
                    </button>
                  ))}
                </div>
              </div>
              <div>
                <label className="text-xs text-paper-ink-3 block mb-1">Email / идентификатор *</label>
                <Input
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={ROLE_TILES.find((t) => t.key === role)?.email}
                />
              </div>
              <div>
                <label className="text-xs text-paper-ink-3 block mb-1">Отображаемое имя</label>
                <Input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder={ROLE_TILES.find((t) => t.key === role)?.name}
                />
              </div>
              <div>
                <label htmlFor="create-office" className="text-xs text-paper-ink-3 block mb-1">
                  Офис *
                </label>
                {/* required здесь декоративен: диалог не обёрнут в <form>, HTML5-валидация
                    не сработает. Настоящая защита — проверки в handleCreate() ниже. */}
                <select
                  id="create-office"
                  required
                  className="w-full h-9 rounded-chip bg-paper-bg px-3 text-sm"
                  value={office}
                  onChange={(e) => setOffice(e.target.value)}
                >
                  <option value="">— выберите офис —</option>
                  {offices.map((o) => (
                    <option key={o.office} value={o.office}>{o.office}</option>
                  ))}
                </select>
              </div>
              <div>
                <label htmlFor="create-owner" className="text-xs text-paper-ink-3 block mb-1">
                  Ответственный за учётку *
                </label>
                <Input
                  id="create-owner"
                  required
                  value={ownerEmail}
                  onChange={(e) => setOwnerEmail(e.target.value)}
                  placeholder="ivanov@1cbit.ru"
                />
              </div>
              {role === 'partner' && (
                <div>
                  <label htmlFor="create-queue-class" className="text-xs text-paper-ink-3 block mb-1">
                    Класс очереди
                  </label>
                  <select
                    id="create-queue-class"
                    className="w-full h-9 rounded-chip bg-paper-bg px-3 text-sm"
                    value={queueClass}
                    onChange={(e) => setQueueClass(e.target.value)}
                  >
                    {QUEUE_CLASSES.map((c) => (
                      <option key={c} value={c}>{QUEUE_CLASS_ICONS[c]} {QUEUE_CLASS_LABELS[c]}</option>
                    ))}
                  </select>
                </div>
              )}
              {role === 'client' && (
                <div>
                  <label htmlFor="create-expiration" className="text-xs text-paper-ink-3 block mb-1">
                    Действителен до *
                  </label>
                  <Input
                    id="create-expiration"
                    type="date"
                    value={expiration}
                    onChange={(e) => setExpiration(e.target.value)}
                  />
                </div>
              )}
              <PersonalQuotaFields
                audio={quotaAudio}
                summary={quotaSummary}
                hourly={quotaHourly}
                onAudioChange={setQuotaAudio}
                onSummaryChange={setQuotaSummary}
                onHourlyChange={setQuotaHourly}
                defaults={defaults}
              />
              {role === 'client' && (
                <p className="text-[11px] text-paper-hint">
                  Тариф новому клиенту выдаётся в его карточке после создания — откройте
                  строку в списке.
                </p>
              )}
            </div>

            {error && (
              <div className="bg-paper-accent/[0.06] text-paper-accent rounded p-3 text-sm">{error}</div>
            )}

            <DialogFooter>
              <Button variant="outline" onClick={handleClose} disabled={creating}>Отмена</Button>
              <Button className="bg-paper-accent hover:bg-paper-accent-hover" onClick={handleCreate} disabled={creating}>
                {creating ? 'Создание...' : 'Создать'}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <div className="space-y-4 py-2">
            <div className="bg-paper-panel rounded-panel p-4">
              <p className="text-sm font-semibold text-paper-warning mb-2">
                Сохраните токен — он больше не будет показан!
              </p>
              <div className="flex items-center gap-2">
                <code className="text-xs bg-paper-card px-3 py-2 rounded-chip flex-1 truncate">
                  {createdToken}
                </code>
                <Button variant="outline" size="sm" onClick={handleCopy}>
                  {copied ? <Check className="w-4 h-4 text-paper-success" /> : <Copy className="w-4 h-4" />}
                </Button>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={handleClose}>Закрыть</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
